import React from 'react';
import { createCodebookCache } from '../utils/codebookCache';
import { CustomCellContext } from '../components/SimpleSpreadsheetHeader';

// ─── Process definitions cache (via shared codebook-cache factory) ───────────
// SWR + subscriber broadcast + auto-invalidate on writes to /api/process-definitions all
// come from the factory — see utils/codebookCache.ts. This used to hand-roll
// `_processDefsCache`/`_inflight`, which had no broadcast: seven screens share this hook,
// so under keep-alive open-tabs the ones that never remounted kept a stale list until F5.

export interface ProcessDefOption {
  _id: string;
  code?: string;
  name: string;
  machines?: Array<{ machine: string | { _id: string; code?: string; name?: string; status?: boolean }; order?: number | string | null }>;
  order?: number;
  status?: string;
}

// /active is open to every module that shows processes (normativ, RN, subprocesses, work-order
// register), not only to the Processes module; it returns the whole list in production order.
const processDefsCache = createCodebookCache<ProcessDefOption>({
  endpoint: '/api/process-definitions/active',
  invalidateOn: '/api/process-definitions',
  select: (data) => (Array.isArray(data) ? data : []),
});

/** Call after any successful create/update/delete so next mount fetches fresh data. */
export const invalidateProcessDefsCache = processDefsCache.invalidate;

export const useProcessDefinitions = (): ProcessDefOption[] => processDefsCache.useOptions();

// ─── Cell components ────────────────────────────────────────────────────────

export const ProcessProcessCell: React.FC<{ ctx: CustomCellContext }> = ({ ctx }) => {
  const processDefs = useProcessDefinitions();
  const value = ctx.row.process || '';
  return (
    <select
      value={value}
      onChange={(e) => ctx.onCellChange(ctx.rowIdx, 'process', e.target.value)}
      onBlur={() => ctx.onFieldBlur(ctx.rowIdx, 'process')}
      onKeyDown={(e) => ctx.handleKeyDown(e, ctx.rowIdx, ctx.actualColIdx)}
      ref={(el) => {
        if (!ctx.inputRefs.current[ctx.rowIdx]) ctx.inputRefs.current[ctx.rowIdx] = [];
        ctx.inputRefs.current[ctx.rowIdx][ctx.actualColIdx] = el;
      }}
      disabled={ctx.isSaving || ctx.disabled}
      className="w-full h-6 px-1 text-sm border-0 outline-none bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
    >
      <option value=""></option>
      {processDefs.map((p) => (
        <option key={p._id} value={p._id}>{p.name}</option>
      ))}
    </select>
  );
};

